import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { native } from "../lib/native";
import { Card } from "./ui/card";
import { Radio, TrendingUp, TrendingDown, Newspaper, Briefcase, Eye, X as XIcon } from "lucide-react";

// Market Pulse News — sub-tabbed news feed for the Dashboard.
//   • All        — every headline picked up from the market wire
//   • Positions  — filtered to symbols in the user's portfolio
//   • Watchlist  — filtered to symbols on the user's watchlist
//
// Clicking a symbol chip below the tabs FILTERS the feed to just that symbol
// (toggle to clear). No navigation — users want to READ the news right here.
export default function SentimentTicker() {
  const [items, setItems] = useState([]);
  const [pulse, setPulse] = useState(0);
  const [tab, setTab] = useState("all");  // all | positions | watchlist
  const [selectedSym, setSelectedSym] = useState(null);
  const [positionSyms, setPositionSyms] = useState(new Set());
  const [watchlistSyms, setWatchlistSyms] = useState(new Set());
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    let alive = true;
    // Fetch user context (positions + watchlist) once — refresh every 5 minutes
    const loadContext = async () => {
      try {
        const [p, w] = await Promise.all([
          api.get("/portfolio").catch(() => ({ data: {} })),
          api.get("/watchlist").catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        const pos = new Set((p.data?.holdings || []).map((h) => (h.symbol || "").toUpperCase()));
        const wl = new Set((w.data || []).map((x) => (x.symbol || "").toUpperCase()));
        setPositionSyms(pos);
        setWatchlistSyms(wl);
      } catch { /* silent */ }
    };
    const load = async () => {
      try {
        const { data } = await api.get("/sentiment/feed?limit=30");
        if (!alive) return;
        const spikes = (data.items || []).filter(
          (x) => x.id && !seenIdsRef.current.has(x.id) && Math.abs(x.sentiment) > 0.6,
        );
        for (const s of spikes) {
          seenIdsRef.current.add(s.id);
          native.haptic(s.sentiment > 0 ? "success" : "warning");
        }
        for (const x of (data.items || [])) if (x.id) seenIdsRef.current.add(x.id);
        setItems(data.items || []);
        setPulse(data.pulse || 0);
      } catch { /* silent */ }
    };
    loadContext();
    load();
    const iv = setInterval(load, 25000);
    const ivCtx = setInterval(loadContext, 300000);
    return () => { alive = false; clearInterval(iv); clearInterval(ivCtx); };
  }, []);

  const pulseColor = pulse > 0.1 ? "text-[hsl(var(--gain))]" : pulse < -0.1 ? "text-[hsl(var(--loss))]" : "text-muted-foreground";
  const PulseIcon = pulse > 0 ? TrendingUp : TrendingDown;

  // Filter items per active tab + symbol chip selection
  const filteredItems = useMemo(() => {
    let base = items;
    if (tab !== "all") {
      const targetSet = tab === "positions" ? positionSyms : watchlistSyms;
      base = base.filter((x) => x.symbol && targetSet.has((x.symbol || "").toUpperCase()));
    }
    if (selectedSym) {
      base = base.filter((x) => (x.symbol || "").toUpperCase() === selectedSym);
    }
    return base;
  }, [items, tab, positionSyms, watchlistSyms, selectedSym]);

  // Chips: derive strictly from the CURRENT news items so a ticker only
  // appears if at least one visible article mentions it. Additionally, the
  // article must represent a REAL CATALYST — earnings, analyst rating changes,
  // product launches, regulator actions, deals, executive changes, dividends,
  // etc. Price-only mover reports ("TSLA gained 3%", "stock up 67 points")
  // are excluded so the strip only surfaces tickers with underlying stories.
  const CATALYST_RE = /\b(earnings?|revenue|guidance|profit|loss|q[1-4]|beats?|miss(?:es|ed)?|eps|forecast|outlook|estimates?|reports?|results?|quarterly|annual|upgrade[ds]?|downgrade[ds]?|rating|price target|analyst|overweight|underweight|neutral|buy rating|sell rating|hold rating|initiates?|reiterates?|coverage|launch(?:es|ed|ing)?|unveils?|debut(?:s|ed)?|announc(?:es|ed|ement)|introduc(?:es|ed)|releases?|ships?|rolls? out|product|feature|fda|sec|doj|ftc|regulator[y]?|approv(?:es|ed|al)|lawsuit|subpoena|investigation|probe|settl(?:es|ement)|fine[ds]?|complian(?:ce|t)|partnership|acquisition|acquires?|merger|merges?|deal|buyout|joint venture|collaboration|invest(?:s|ment|or)|stake|ceo|cfo|coo|hires?|resigns?|fires?|appoint(?:s|ed|ment)|leadership|dividend|buyback|split|ipo|spinoff|spins? off|contract|wins?|awarded|expand(?:s|ed|ing|sion)?|layoff|restructur|cut(?:s|ting)? jobs|hir(?:ing|es)|patent|breakthrough|breach|hack|cyberattack|recall|delist|halt|suspend|delivers?|shipm|milestone|record .* (?:sales|orders|revenue|units)|guidance|raises? .* (?:guidance|outlook|target)|slashes? .* (?:guidance|outlook|target)|s&p 500|nasdaq[- ]100|index inclusion|joining .* index|russell)\b/i;

  // Explicit price-only movement pattern — used as a NEGATIVE signal.
  // If a title matches this AND contains no catalyst keyword, we still drop it.
  // (Redundant with the require-catalyst rule but safer against edge cases.)
  const PRICE_ONLY_RE = /^\s*[a-z]{1,6}\s+(?:up|down|gains?|loses?|rose|rise[sn]?|dips?|drops?|jumps?|climbs?|falls?|surges?|plunges?|tumbles?)\s+\d/i;

  const isCatalyst = (it) => {
    const text = `${it.title || ""} ${it.body || ""}`;
    if (!CATALYST_RE.test(text)) return false;
    if (PRICE_ONLY_RE.test(it.title || "")) return false;
    return true;
  };

  const symbolChips = useMemo(() => {
    const buckets = new Map();   // symbol → { sum, n }
    for (const it of items) {
      if (!it.symbol) continue;
      // Only news-sourced items can contribute a chip. `markets` (price-only
      // movers) and `signals` (internal alert fires) don't count as catalysts.
      if (it.source && it.source !== "news") continue;
      if (!isCatalyst(it)) continue;
      const sym = it.symbol.toUpperCase();
      if (tab === "positions" && !positionSyms.has(sym)) continue;
      if (tab === "watchlist" && !watchlistSyms.has(sym)) continue;
      const b = buckets.get(sym) || { sum: 0, n: 0 };
      b.sum += Number(it.sentiment || 0);
      b.n += 1;
      buckets.set(sym, b);
    }
    return Array.from(buckets.entries())
      .map(([symbol, { sum, n }]) => ({ symbol, curr: sum / n, count: n }))
      .sort((a, b) => Math.abs(b.curr) - Math.abs(a.curr))
      .slice(0, 8);
  }, [items, tab, positionSyms, watchlistSyms]);   // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: "all",       label: "All",       icon: Newspaper,  count: items.length },
    { id: "positions", label: "Positions", icon: Briefcase,  count: items.filter((x) => x.symbol && positionSyms.has(x.symbol.toUpperCase())).length },
    { id: "watchlist", label: "Watchlist", icon: Eye,        count: items.filter((x) => x.symbol && watchlistSyms.has(x.symbol.toUpperCase())).length },
  ];

  const emptyCopy = selectedSym
    ? `No news mentioning ${selectedSym} in this poll — check back in a minute.`
    : tab === "positions"
    ? (positionSyms.size === 0 ? "No positions yet — buy something and news will show here." : "No news matched your positions in this poll.")
    : tab === "watchlist"
    ? (watchlistSyms.size === 0 ? "Your watchlist is empty — add symbols on the Markets page." : "No news matched your watchlist in this poll.")
    : "Watching the tape for signals…";

  // Reset symbol filter when the tab changes
  const switchTab = (id) => {
    setTab(id);
    setSelectedSym(null);
  };

  return (
    <Card className="p-4 rounded-2xl overflow-hidden" data-testid="sentiment-ticker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-[hsl(var(--gain))] animate-pulse" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Market Pulse News</span>
          <span
            title={"Chip color reflects the news sentiment for that ticker:\n"
                   + "  green = bullish (positive catalysts)\n"
                   + "  red   = bearish (negative catalysts)\n"
                   + "  gray  = neutral / mixed\n"
                   + "The number is the sentiment score ×100 (range: −100 to +100)."}
            data-testid="pulse-info"
            className="text-[10px] text-muted-foreground hover:text-foreground cursor-help border border-border/60 rounded-full w-4 h-4 flex items-center justify-center leading-none"
          >
            ?
          </span>
        </div>
        <div className={`text-sm font-mono-tab flex items-center gap-1 ${pulseColor}`} data-testid="pulse-value">
          <PulseIcon className="w-4 h-4" />
          {(pulse * 100).toFixed(0)}
        </div>
      </div>

      {/* Sub-category tabs */}
      <div className="flex items-center gap-1 mb-3 -mx-1 overflow-x-auto no-scrollbar" data-testid="pulse-tabs">
        {tabs.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => switchTab(t.id)}
              data-testid={`pulse-tab-${t.id}`}
              className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors flex-shrink-0 ${
                active
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] font-mono-tab ${active ? "opacity-70" : "opacity-50"}`}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {symbolChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-border/50" data-testid="symbol-pulse-chips">
          {symbolChips.map((c) => {
            const isSelected = selectedSym === c.symbol;
            const base = c.curr > 0.3 ? "border-[hsl(var(--gain))]/40 text-[hsl(var(--gain))] bg-[hsl(var(--gain))]/5"
              : c.curr < -0.3 ? "border-[hsl(var(--loss))]/40 text-[hsl(var(--loss))] bg-[hsl(var(--loss))]/5"
              : "border-border text-muted-foreground";
            const activeCls = isSelected ? "!bg-foreground !text-background !border-foreground" : "";
            const tone = c.curr > 0.3 ? "bullish" : c.curr < -0.3 ? "bearish" : "neutral";
            const scoreLabel = `${c.curr > 0 ? "+" : ""}${(c.curr * 100).toFixed(0)}`;
            return (
              <button
                key={c.symbol}
                type="button"
                onClick={() => setSelectedSym(isSelected ? null : c.symbol)}
                title={`${c.symbol} · ${tone} sentiment (score ${scoreLabel})\n${c.count} catalyst mention${c.count === 1 ? "" : "s"} in current news\n\nGreen = bullish, red = bearish, gray = neutral.\nClick to filter the feed to just ${c.symbol}.`}
                data-testid={`symbol-chip-${c.symbol}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-mono-tab border transition-all ${base} ${activeCls} hover:opacity-90`}
              >
                {c.symbol} <span className="ml-0.5 font-semibold">{scoreLabel}</span>
              </button>
            );
          })}
          {selectedSym && (
            <button
              type="button"
              onClick={() => setSelectedSym(null)}
              data-testid="symbol-chip-clear"
              className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-mono-tab border border-dashed border-muted-foreground/40 text-muted-foreground hover:text-foreground hover:border-foreground/40 flex items-center gap-1"
            >
              <XIcon className="w-2.5 h-2.5" /> Clear
            </button>
          )}
        </div>
      )}

      {filteredItems.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2" data-testid="pulse-empty">{emptyCopy}</div>
      ) : (
        <div className="space-y-1.5" data-testid="pulse-items">
          {filteredItems.slice(0, 8).map((it) => (
            <a
              key={it.id}
              href={it.link || "#"}
              target={it.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="flex items-start gap-2 text-xs hover:bg-secondary/30 -mx-1 px-1 py-1 rounded transition-colors block"
              data-testid={`pulse-item-${it.id}`}
            >
              <div className={`w-1 h-1 rounded-full mt-1.5 flex-shrink-0 ${
                it.sentiment > 0.3 ? "bg-[hsl(var(--gain))]" :
                it.sentiment < -0.3 ? "bg-[hsl(var(--loss))]" : "bg-muted-foreground"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.title}</div>
                {it.body && <div className="text-muted-foreground text-[10px] truncate">{it.body}</div>}
              </div>
              {it.symbol && (
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-mono-tab flex-shrink-0">
                  {it.symbol}
                </div>
              )}
            </a>
          ))}
        </div>
      )}
    </Card>
  );
}
